import {
  CastReceiverContext,
  PlayerManager,
} from 'chromecast-caf-receiver/cast.framework';
import {
  EventType,
  ErrorEvent,
} from 'chromecast-caf-receiver/cast.framework.events';
import { EventType as SystemEventType } from 'chromecast-caf-receiver/cast.framework.system';
import { PlayerState } from 'chromecast-caf-receiver/cast.framework.messages';
import type { CastSession } from '../types/session';
import { StateController, AppState } from '../state-controller';
import { Analytics } from '../analytics';
import { Logger } from '../logger';
import { datadogError } from '../monitoring/datadog';
import { emitMuxError } from '../monitoring/mux';
import { buildPlayerErrorMessage } from '../monitoring/error-codes';

/**
 * Event listeners — wires playerManager and context events to Analytics + Logger.
 *
 * Ported from `legacy/js/receiver.js` event listener registrations. This module
 * is the central wiring point for all Cast events that need Analytics tracking
 * and structured logging.
 *
 * **Note:** Some events are also handled by dedicated modules:
 * - `checkpoint.ts` (4.4) — PLAYING, PLAY, PAUSE, ENDED, ERROR, ABORT for interval control
 * - `tracks-handler.ts` (4.5) — PLAYER_LOAD_COMPLETE for track enumeration
 * - `media-status.ts` (4.6) — ENDED for `completeMedia()` / `update_ended`
 * - `queue-manager.ts` (4.3) — LOADED_DATA for next episode prefetch
 *
 * Multiple listeners on the same event are supported by the Cast SDK.
 * This module only adds Analytics + Logger; business logic lives in the
 * dedicated modules above.
 *
 * **Differences from legacy:**
 * - Structured logging via `Logger` with event data
 * - Typed event handlers (ErrorEvent, etc.)
 * - Datadog integration for error observability (seção 8.3)
 * - No duplicate listeners (legacy had REQUEST_VOLUME_CHANGE registered twice)
 */

const LOG_TAG = 'EVENTS';

const { eventNames } = Analytics;

// ---------------------------------------------------------------------------
// Player event helpers
// ---------------------------------------------------------------------------

/**
 * Handles the ERROR event from the playerManager.
 *
 * - Logs the error to Analytics with detailed error codes
 * - Logs to Logger for structured output
 * - Forwards to Datadog for observability (seção 8.3)
 */
function handlePlayerError(
  event: ErrorEvent,
  session: CastSession,
): void {
  const detailedErrorCode = event.detailedErrorCode;
  const shakaErrorCode =
    (event.error as { shakaErrorCode?: number } | undefined)?.shakaErrorCode;

  const mediaId =
    (session.loadRequestData?.media.metadata as { customData?: { mediaId?: string } } | undefined)
      ?.customData?.mediaId;

  // Readable message shared across observability sinks (Mux, Datadog, Logger).
  const message = buildPlayerErrorMessage({
    detailedErrorCode,
    shakaErrorCode,
    mediaId,
  });

  // Analytics
  Analytics.logEvent(eventNames.MEDIA_REPRODUCTION_ERROR, {
    error: detailedErrorCode,
    shaka_error_code: shakaErrorCode,
  });

  // Logger
  Logger.error(LOG_TAG, `Player ERROR — ${message}`, {
    detailedErrorCode,
    shakaErrorCode,
  });

  // Mux — replaces the SDK's automatic (message-less) error tracking.
  emitMuxError(detailedErrorCode, message, mediaId);

  // Datadog
  datadogError('PLAYER', event.error ?? new Error(message), {
    detailedErrorCode,
    shakaErrorCode,
    mediaId,
  });

  // -- DRM error 420: HDCP output protection refused --
  // No retry: `drm_license_v_2` licenses are always issued with HDCP off
  // (argos hardcodes `hdcp false` on the v2 acquisition path), so replaying
  // the LOAD cannot produce a different license.
  if (detailedErrorCode === 420) {
    Logger.error(
      LOG_TAG,
      'DRM 420: HDCP output protection refused by the device/display',
    );
  }

  // -- LOAD_FAILED 905: additional context --
  if (detailedErrorCode === 905) {
    Logger.error(
      LOG_TAG,
      'LOAD_FAILED (905): verify load request and media playability',
    );
  }
}

// ---------------------------------------------------------------------------
// Registration: playerManager events
// ---------------------------------------------------------------------------

/**
 * Registers Analytics + Logger listeners for playerManager events.
 *
 * Events wired:
 * - PLAY, PAUSE, SEEKING, BUFFERING
 * - ERROR (with DRM 420 retry logic)
 * - REQUEST_VOLUME_CHANGE, REQUEST_EDIT_TRACKS_INFO, REQUEST_EDIT_AUDIO_TRACKS
 * - PLAYER_LOAD_COMPLETE (analytics only — track handler is separate)
 */
function registerPlayerEvents(
  playerManager: PlayerManager,
  session: CastSession,
  castState: StateController,
): void {
  // -- PLAY --
  playerManager.addEventListener(EventType.PLAY, () => {
    Analytics.logEvent(eventNames.PLAYER_PLAY);
    Logger.debug(LOG_TAG, 'PLAY');
  });

  // -- PAUSE --
  playerManager.addEventListener(EventType.PAUSE, () => {
    Analytics.logEvent(eventNames.PLAYER_PAUSE);
    Logger.debug(LOG_TAG, 'PAUSE');
  });

  // -- SEEKING --
  playerManager.addEventListener(EventType.SEEKING, () => {
    Analytics.logEvent(eventNames.MEDIA_SEEK);
    Logger.debug(LOG_TAG, 'SEEKING');
  });

  // -- BUFFERING --
  playerManager.addEventListener(EventType.BUFFERING, () => {
    Analytics.logEvent(eventNames.CAST_BUFFERING);
    Logger.debug(LOG_TAG, 'BUFFERING');
  });

  // -- ERROR --
  playerManager.addEventListener(EventType.ERROR, (event) => {
    handlePlayerError(event as ErrorEvent, session);
  });

  // -- REQUEST_VOLUME_CHANGE --
  playerManager.addEventListener(EventType.REQUEST_VOLUME_CHANGE, () => {
    Analytics.logEvent(eventNames.CAST_REQUEST_VOLUME_CHANGE);
    Logger.debug(LOG_TAG, 'REQUEST_VOLUME_CHANGE');
  });

  // -- REQUEST_EDIT_TRACKS_INFO --
  playerManager.addEventListener(EventType.REQUEST_EDIT_TRACKS_INFO, () => {
    Analytics.logEvent(eventNames.CAST_REQUEST_EDIT_TRACKS_INFO);
    Logger.debug(LOG_TAG, 'REQUEST_EDIT_TRACKS_INFO');
  });

  // -- REQUEST_EDIT_AUDIO_TRACKS --
  playerManager.addEventListener(EventType.REQUEST_EDIT_AUDIO_TRACKS, () => {
    Analytics.logEvent(eventNames.CAST_REQUEST_EDIT_AUDIO_TRACKS);
    Logger.debug(LOG_TAG, 'REQUEST_EDIT_AUDIO_TRACKS');
  });

  // -- PLAYER_LOAD_COMPLETE (analytics + hide standby) --
  playerManager.addEventListener(EventType.PLAYER_LOAD_COMPLETE, () => {
    Analytics.logEvent(eventNames.CAST_PLAYER_LOAD_COMPLETE);
    Logger.debug(LOG_TAG, 'PLAYER_LOAD_COMPLETE');
    castState.set(AppState.PLAYING);
  });

  // -- MEDIA_STATUS → return to IDLE when player becomes idle after playback --
  playerManager.addEventListener(EventType.MEDIA_STATUS, () => {
    const state = playerManager.getPlayerState();
    if (state === PlayerState.IDLE && castState.current === AppState.PLAYING) {
      castState.set(AppState.IDLE);
      Logger.debug(LOG_TAG, 'Player idle — back to standby');
    }
  });

  Logger.info(LOG_TAG, 'Player event listeners registered');
}

// ---------------------------------------------------------------------------
// Registration: context (system) events
// ---------------------------------------------------------------------------

/**
 * Registers Analytics + Logger listeners for CastReceiverContext system events.
 *
 * Events wired:
 * - READY, ERROR, SENDER_CONNECTED, SENDER_DISCONNECTED
 * - SHUTDOWN, STANDBY_CHANGED, SYSTEM_VOLUME_CHANGED
 */
function registerContextEvents(context: CastReceiverContext): void {
  // -- READY --
  context.addEventListener(SystemEventType.READY, () => {
    Analytics.logEvent(eventNames.CAST_CONTEXT_READY);
    Logger.info(LOG_TAG, 'Context READY');
  });

  // -- ERROR --
  context.addEventListener(SystemEventType.ERROR, () => {
    Analytics.logEvent(eventNames.CAST_CONTEXT_ERROR);
    Logger.error(LOG_TAG, 'Context ERROR');
  });

  // -- SENDER_CONNECTED --
  context.addEventListener(SystemEventType.SENDER_CONNECTED, () => {
    Analytics.logEvent(eventNames.CAST_SENDER_CONNECTED);
    Logger.info(LOG_TAG, 'Sender connected');
  });

  // -- SENDER_DISCONNECTED --
  context.addEventListener(SystemEventType.SENDER_DISCONNECTED, () => {
    Analytics.logEvent(eventNames.CAST_SENDER_DISCONNECTED);
    Logger.info(LOG_TAG, 'Sender disconnected');
  });

  // -- SHUTDOWN --
  context.addEventListener(SystemEventType.SHUTDOWN, () => {
    Analytics.logEvent(eventNames.CAST_SHUTDOWN);
    Logger.info(LOG_TAG, 'SHUTDOWN');
  });

  // -- STANDBY_CHANGED --
  context.addEventListener(SystemEventType.STANDBY_CHANGED, () => {
    Analytics.logEvent(eventNames.CAST_STANDBY_CHANGED);
    Logger.debug(LOG_TAG, 'STANDBY_CHANGED');
  });

  // -- SYSTEM_VOLUME_CHANGED --
  context.addEventListener(SystemEventType.SYSTEM_VOLUME_CHANGED, () => {
    Analytics.logEvent(eventNames.CAST_SYSTEM_VOLUME_CHANGED);
    Logger.debug(LOG_TAG, 'SYSTEM_VOLUME_CHANGED');
  });

  Logger.info(LOG_TAG, 'Context event listeners registered');
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Registers all event listeners for Analytics + Logger on both
 * the playerManager and the CastReceiverContext.
 *
 * @param playerManager - Cast player manager instance
 * @param context       - Cast receiver context instance
 * @param session       - Session state (used for DRM 420 retry logic)
 * @param castState     - App state controller (for UI transitions)
 */
export function registerEventListeners(
  playerManager: PlayerManager,
  context: CastReceiverContext,
  session: CastSession,
  castState: StateController,
): void {
  registerPlayerEvents(playerManager, session, castState);
  registerContextEvents(context);
}
